import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { TextField } from '@/components/ui/TextField'
import { useCreateLounge } from '@/features/lounges/api'
import { errorMessage } from '@/lib/graphql'

/**
 * Create a lounge.
 *
 * A route rather than an `isOpen` boolean: deep-linkable, survives a refresh,
 * and the back button closes it.
 */
export const Route = createFileRoute('/_app/lounges/new')({
  component: CreateLoungeModal,
})

function CreateLoungeModal() {
  const navigate = useNavigate()
  const createLounge = useCreateLounge()
  const [name, setName] = useState('')

  const close = () => void navigate({ to: '/' })

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    createLounge.mutate(name, {
      onSuccess: (data) => {
        void navigate({
          to: '/l/$loungeSlug',
          params: { loungeSlug: data.createLounge.slug },
        })
      },
    })
  }

  return (
    <Dialog
      open
      onClose={close}
      title="Create a lounge"
      description="A lounge is a community — a place for one group of people. You will be its owner."
    >
      <form onSubmit={submit}>
        <div className="dialog-body">
          <TextField
            label="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="The Porch"
            hint="The web address is made from this, and stays put if you rename it later."
            autoFocus
            required
          />

          {createLounge.isError && (
            <p className="form-error" role="alert">
              {errorMessage(createLounge.error, 'That lounge could not be created.')}
            </p>
          )}
        </div>

        <div className="dialog-actions">
          <Button onClick={close}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={createLounge.isPending}>
            {createLounge.isPending ? 'Creating…' : 'Create lounge'}
          </Button>
        </div>
      </form>
    </Dialog>
  )
}
